import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import QuestionCard from "@/components/site/QuestionCard";

const ACCENT = "#004D40"; // teal

const QUESTIONS = [
  {
    q: "Someone selling an old phone online is asked by the 'buyer' to scan a QR code to receive payment. Does a teenager know that scanning only ever sends money — never receives it?",
    f: "This single misunderstanding is how thousands lose money every month. The rule, once known, defeats the scam permanently.",
  },
  {
    q: "A government certificate is needed urgently. Does a 14-year-old know it can be done online for about ₹30 — not ₹800 through an agent?",
    f: "Most families don't know the portal exists, and quietly overpay agents for services that are nearly free.",
  },
  {
    q: "A bank quotes one person a higher loan rate than their neighbour for the same loan. Does anyone leaving school know why — and that a number called a credit score caused it?",
    f: "A weak score can cost several lakh in extra interest over a home loan. Most people first hear of it the day they're refused.",
  },
  {
    q: "A shopkeeper charges more than the printed MRP. Does a teenager know that is actually illegal?",
    f: "MRP is the maximum legal price by law. Knowing this one fact turns a quiet acceptance into a confident refusal.",
  },
  {
    q: "A defective product is bought and the seller refuses a refund. Does a young person know the free helpline that resolves this in weeks?",
    f: "The National Consumer Helpline resolves lakhs of complaints — used only by the small minority who know it exists.",
  },
  {
    q: "A 'caller from the bank' asks for an OTP to 'verify' an account. Does a teenager know that no real bank ever does this?",
    f: "An OTP is the key to the account. Sharing it hands over everything. The rule is absolute: an OTP is never spoken aloud to anyone.",
  },
  {
    q: "A neighbourhood problem — a broken pipe, an encroached park — goes ignored for months. Does a teenager know that one citizen can file an RTI for ₹10 and force an answer?",
    f: "A schoolchild has stopped an illegal encroachment this way. The tool exists for everyone; almost no one uses it.",
  },
];

const AREAS = [
  { tag: "A", t: "Banking, Money & Payments", d: "UPI safety, bank accounts, loans, insurance, taxes." },
  { tag: "B", t: "Government Services & Rights", d: "MeeSeva, land records, police and FIRs, RTI, identity documents." },
  { tag: "C", t: "Consumer Rights", d: "The Consumer Protection Act, online shopping, telecom, healthcare bills, renting." },
  { tag: "D", t: "Cyber Fraud Defence", d: "UPI scams, social media fraud, investment scams, emergency services." },
  { tag: "E", t: "Social Navigation", d: "Negotiation, travel and transport rights, work and pay, spotting misinformation, civic action." },
];

export default function Level2() {
  useReveal();
  useEffect(() => { document.title = "Level 2 — Society · LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section
        className="bg-[#0D1B2A] text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,42,0.95) 0%, rgba(13,27,42,0.97) 100%), url('https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=1920&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: ACCENT }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#F57F17" }}>
              Level 2 · System Intelligence · Age 12–14
            </div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[64px] leading-[1.05] tracking-tight">
              Can a teenager deal with the world without being cheated — or sent to a middleman?
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              Banks, government offices, landlords, shopkeepers, and fraudsters all assume the person in front of them doesn't know their rights. Level 2 makes sure a 12 to 14-year-old does — so they navigate the outside world on their own terms.
            </p>
            <p className="mt-6 font-serif-d italic text-[17px] sm:text-[19px] leading-[1.5] max-w-3xl" style={{ color: "#7BC47F" }}>
              System Intelligence — closing the gap between a student and the systems that shape society.
            </p>
          </div>
        </div>
      </section>

      {/* DISCOVERY QUESTIONS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Before we explain Level 2 — try these.</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              The systems that run adult life.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              These are everyday encounters with the systems that run adult life. Notice how many have a rule, a right, or a defence that almost no one is taught.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => <QuestionCard key={i} q={c.q} f={c.f} i={i} accent={ACCENT} />)}
          </div>

          <div className="mt-16 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[32px] leading-[1.3]" style={{ color: ACCENT }}>
              Each of these has a rule, a right, or a defence that can be learned and practised. That is Level 2 — the world, made navigable.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT LEVEL 2 COVERS */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What Level 2 covers</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Five areas. One outcome: a young person no system can take advantage of.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {AREAS.map((a, i) => (
              <div key={a.tag} data-testid={`area-${a.tag}`} className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 flex flex-col" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="h-1 w-10" style={{ backgroundColor: ACCENT }} />
                <div className="mt-5 text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Area {a.tag}</div>
                <h3 className="mt-2 font-serif-d text-[22px] text-[#0D1B2A] leading-tight">{a.t}</h3>
                <p className="mt-3 text-[15px] text-[#5C6470] leading-[1.65]">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOME */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">By the end of Level 2</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08] text-white">
              A capable citizen, not an easy target.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              A teenager can navigate the outside world without a middleman — or at the very least, knows exactly what they are paying one for. They can open an account, verify a website, refuse a fraud, claim a right, and reach the correct authority. They stop being an easy target and start being a capable citizen.
            </p>
            <Link to="/level-3" data-testid="level2-next" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              See Level 3 — Life Design <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
